import {
  type ChangeEventHandler,
  type FocusEventHandler,
  type ReactNode,
  useEffect,
  useMemo,
} from "react";
import { zodResolver } from "@hookform/resolvers/zod";
import { Controller, FormProvider, type FieldPath, useForm } from "react-hook-form";
import { TriangleAlert } from "lucide-react";
import { useTranslation } from "react-i18next";
import { z } from "zod";

import { Alert, AlertDescription } from "@/components/ui/alert";
import { WFPButton } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { EmailField } from "@/components/ui/email-field";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Spinner } from "@/components/ui/spinner";
import { cn } from "@/lib/utils";
import { emailSchema } from "@/lib/validation";
import { MCF_COOPERATING_PARTNER_ORG_TYPE } from "../../../../../../constants";
import { getCountryObjectFromCache } from "../../../../../../utils";

const optionalSelectOptionSchema = z
  .object({
    value: z.union([z.string(), z.number()]).optional(),
    label: z.string().optional(),
  })
  .passthrough()
  .nullable()
  .optional();

const requiredString = (message: string) =>
  z
    .string()
    .optional()
    .refine(value => !!value, { message });

const createAdministratorSchema = (requiredMessage: string, invalidEmailMessage: string) =>
  z
    .object({
      uuid: z.string().optional(),
      first_name: requiredString(requiredMessage),
      middle_name: z.string().optional(),
      last_name: requiredString(requiredMessage),
      contact_phone: z.string().optional(),
      identification_card_number: z.string().optional(),
      gender: optionalSelectOptionSchema,
      year_of_birth: z.union([z.string(), z.number()]).optional(),
      email: emailSchema({ required: requiredMessage, invalid: invalidEmailMessage }),
      role: z.string().optional(),
      account_type: z.string().optional(),
      active: z.boolean().optional(),
    })
    .passthrough();

type SelectOption = z.infer<typeof optionalSelectOptionSchema>;
export type AdministratorFormValues = z.infer<ReturnType<typeof createAdministratorSchema>>;

interface AggregatorFormValues {
  org_type?: SelectOption | null;
  [key: string]: unknown;
}

interface CountryDetails {
  flag?: string;
  phone_prefix?: string | number;
}

interface AddAdministratorFormProps {
  id?: number | string;
  isFetching: boolean;
  errorMessage?: string;
  onClickValidate: (values: AdministratorFormValues) => void;
  isOnEdit: boolean;
  onClickClose: () => void;
  initialValues?: Partial<AdministratorFormValues>;
  fieldErrors?: Record<string, unknown>;
  aggregator: AggregatorFormValues;
}

interface FieldShellProps {
  id: string;
  label: string;
  children: ReactNode;
  errorMessage?: string;
  required?: boolean;
  className?: string;
}

interface PhoneNumberInputProps {
  id: string;
  value?: string;
  placeholder?: string;
  disabled?: boolean;
  hasError?: boolean;
  onChange: ChangeEventHandler<HTMLInputElement>;
  onBlur: FocusEventHandler<HTMLInputElement>;
  name: string;
}

const toErrorMessage = (error: unknown) => {
  if (!error) {
    return "";
  }

  if (typeof error === "string") {
    return error;
  }

  if (Array.isArray(error)) {
    return error.join(", ");
  }

  if (typeof error === "object" && "message" in error) {
    const message = (error as { message?: unknown }).message;
    return typeof message === "string" ? message : "";
  }

  return String(error);
};

const FieldShell = ({
  id,
  label,
  children,
  errorMessage,
  required = false,
  className,
}: FieldShellProps) => (
  <div className={cn("space-y-2", className)}>
    <Label htmlFor={id} className="font-semibold text-foreground">
      {label}
      {required && (
        <span aria-hidden="true" className="ml-0.5 text-destructive">
          *
        </span>
      )}
    </Label>
    {children}
    {errorMessage && (
      <p id={`${id}-error`} role="alert" className="text-destructive text-sm">
        {errorMessage}
      </p>
    )}
  </div>
);

const PhoneNumberInput = ({
  id,
  value,
  placeholder,
  disabled = false,
  hasError = false,
  onChange,
  onBlur,
  name,
}: PhoneNumberInputProps) => {
  const country = getCountryObjectFromCache() as CountryDetails;
  const prefix = country.phone_prefix ? `+${country.phone_prefix}` : "";

  return (
    <div
      className={cn(
        "flex h-9 overflow-hidden rounded-md border border-border bg-background transition-[color,box-shadow]",
        "focus-within:border-ring focus-within:ring-[3px] focus-within:ring-ring/50",
        hasError && "border-destructive ring-destructive/20",
        disabled && "opacity-50"
      )}
    >
      {(country.flag || prefix) && (
        <div className="flex shrink-0 items-center gap-1 border-r border-border bg-neutral-alpha-50 px-3 text-sm">
          {country.flag && <span aria-hidden="true">{country.flag}</span>}
          {prefix && <span>{prefix}</span>}
        </div>
      )}
      <input
        id={id}
        name={name}
        type="tel"
        value={value ?? ""}
        onChange={onChange}
        onBlur={onBlur}
        placeholder={placeholder}
        disabled={disabled}
        aria-invalid={hasError}
        aria-describedby={hasError ? `${id}-error` : undefined}
        className="min-w-0 flex-1 bg-transparent px-3 py-1 text-base outline-none placeholder:text-muted-foreground disabled:cursor-not-allowed md:text-sm"
      />
    </div>
  );
};

const FormError = ({ message }: { message?: string }) => {
  if (!message) {
    return null;
  }

  return (
    <Alert variant="destructive">
      <TriangleAlert className="size-4" />
      <AlertDescription>{message}</AlertDescription>
    </Alert>
  );
};

// A form used to create and edit an Aggregator Administrator
export const AddAdministratorForm = ({
  isFetching,
  errorMessage = "",
  onClickValidate,
  isOnEdit,
  onClickClose,
  initialValues = {},
  fieldErrors = {},
  aggregator,
}: AddAdministratorFormProps) => {
  const { t } = useTranslation();
  const schema = useMemo(
    () =>
      createAdministratorSchema(
        t("traderDetails.form.mandatoryFieldBlankError"),
        t("traderDetails.form.invalidEmailError")
      ),
    [t]
  );
  const methods = useForm<AdministratorFormValues>({
    defaultValues: initialValues,
    mode: "onSubmit",
    reValidateMode: "onChange",
    resolver: zodResolver(schema),
  });
  const {
    control,
    handleSubmit,
    reset,
    setError,
    clearErrors,
    formState: { errors },
  } = methods;

  useEffect(() => {
    if (Object.keys(fieldErrors).length === 0) {
      clearErrors();
      reset(initialValues);
      return;
    }

    clearErrors();
    Object.entries(fieldErrors).forEach(([fieldName, message]) => {
      setError(fieldName as FieldPath<AdministratorFormValues>, {
        type: "server",
        message: toErrorMessage(message),
      });
    });
  }, [clearErrors, fieldErrors, initialValues, reset, setError]);

  const handleAdministratorCreation = handleSubmit(form => {
    const result = { ...form };
    const isMcfAggregator = aggregator.org_type?.label === MCF_COOPERATING_PARTNER_ORG_TYPE;

    result.account_type = isMcfAggregator ? "mastercard_foundation_admin" : "aggregator_admin";

    onClickValidate(result);
  });

  const getFieldError = (fieldName: FieldPath<AdministratorFormValues>) => {
    const error = errors[fieldName] as { message?: unknown } | undefined;
    return toErrorMessage(error?.message);
  };

  if (isFetching) {
    return (
      <Card className="mb-8 border-neutral-alpha-200 bg-background shadow-none">
        <CardContent className="flex min-h-32 items-center justify-center">
          <Spinner className="size-6 text-primary" />
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="mb-8 border-neutral-alpha-200 bg-background shadow-none">
      <CardContent>
        <FormProvider {...methods}>
          <form className="space-y-6" noValidate onSubmit={handleAdministratorCreation}>
            <FormError message={errorMessage} />

            <div className="grid gap-5 md:grid-cols-2">
              <Controller
                control={control}
                name="first_name"
                render={({ field }) => {
                  const error = getFieldError("first_name");

                  return (
                    <FieldShell
                      id="first_name-input"
                      label={t("aggregatorDetails.administratorModal.firstName")}
                      errorMessage={error}
                      required
                    >
                      <Input
                        {...field}
                        id="first_name-input"
                        value={field.value ?? ""}
                        aria-invalid={!!error}
                        aria-describedby={error ? "first_name-input-error" : undefined}
                        className="border-border"
                      />
                    </FieldShell>
                  );
                }}
              />

              <Controller
                control={control}
                name="last_name"
                render={({ field }) => {
                  const error = getFieldError("last_name");

                  return (
                    <FieldShell
                      id="last_name-input"
                      label={t("aggregatorDetails.administratorModal.lastName")}
                      errorMessage={error}
                      required
                    >
                      <Input
                        {...field}
                        id="last_name-input"
                        value={field.value ?? ""}
                        aria-invalid={!!error}
                        aria-describedby={error ? "last_name-input-error" : undefined}
                        className="border-border"
                      />
                    </FieldShell>
                  );
                }}
              />
            </div>

            <EmailField
              control={control}
              name="email"
              label={t("aggregatorDetails.administratorModal.email")}
              disabled={isOnEdit}
              required
            />

            <div className="grid gap-5 md:grid-cols-2">
              <Controller
                control={control}
                name="contact_phone"
                render={({ field }) => {
                  const error = getFieldError("contact_phone");

                  return (
                    <FieldShell
                      id="contact_phone-input"
                      label={t("aggregatorDetails.administratorModal.cellNumber")}
                      errorMessage={error}
                    >
                      <PhoneNumberInput
                        id="contact_phone-input"
                        name={field.name}
                        value={field.value}
                        onBlur={field.onBlur}
                        onChange={field.onChange}
                        hasError={!!error}
                      />
                    </FieldShell>
                  );
                }}
              />

              <Controller
                control={control}
                name="role"
                render={({ field }) => {
                  const error = getFieldError("role");

                  return (
                    <FieldShell
                      id="role-input"
                      label={t("aggregatorDetails.administratorModal.role")}
                      errorMessage={error}
                    >
                      <Input
                        {...field}
                        id="role-input"
                        value={field.value ?? ""}
                        aria-invalid={!!error}
                        aria-describedby={error ? "role-input-error" : undefined}
                        className="border-border"
                      />
                    </FieldShell>
                  );
                }}
              />
            </div>

            <div className="flex justify-end gap-3 pt-2">
              {isOnEdit && (
                <WFPButton
                  type="button"
                  variant="secondary"
                  onClick={() => {
                    reset({});
                    onClickClose();
                  }}
                >
                  {t("common.clearAll")}
                </WFPButton>
              )}
              <WFPButton type="submit">
                {isOnEdit
                  ? t("aggregatorDetails.administratorModal.updateAdministrator")
                  : t("aggregatorDetails.administratorModal.createAdministrator")}
              </WFPButton>
            </div>
          </form>
        </FormProvider>
      </CardContent>
    </Card>
  );
};
